import { useState } from "react";
import { ExerciseGif } from "../../components/ui/ExerciseGif";
import { CtaButton } from "../../components/ui/Button";
import { useExerciseCatalog, MUSCLES } from "./useExerciseCatalog";
import type { LibraryExercise } from "../../data/exerciseLibrary";

interface Targets {
  sets: number;
  repsMin: number;
  repsMax: number;
  rir: number | null;
}

interface Props {
  onClose: () => void;
  onAdd: (entry: LibraryExercise, targets: Targets, dayLabel: string | null) => Promise<void>;
  dayLabel?: string | null;
}

export function ExercisePicker({ onClose, onAdd, dayLabel = null }: Props) {
  const [search, setSearch] = useState("");
  const [muscle, setMuscle] = useState<string | null>(null);
  const [selected, setSelected] = useState<LibraryExercise | null>(null);
  const { results } = useExerciseCatalog(search, muscle);

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-[430px] flex-col bg-bg0">
      {selected ? (
        <ExerciseDetail
          entry={selected}
          dayLabel={dayLabel}
          onBack={() => setSelected(null)}
          onClose={onClose}
          onAdd={onAdd}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 border-b border-line p-3.5">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ejercicio…"
              className="flex-1 rounded-inner border border-line bg-white/[.045] px-3.5 py-2.5 text-[14.5px] text-text outline-none focus:border-accent"
            />
            <button onClick={onClose} className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
              Cerrar
            </button>
          </div>

          <div className="flex gap-1.5 overflow-x-auto border-b border-line p-2.5">
            <button
              onClick={() => setMuscle(null)}
              className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-[10px] tracking-[0.06em] ${
                muscle === null ? "border-accent bg-accent text-white" : "border-line text-muted"
              }`}
            >
              Todos
            </button>
            {MUSCLES.map((m) => (
              <button
                key={m}
                onClick={() => setMuscle(m)}
                className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-[10px] tracking-[0.06em] capitalize ${
                  muscle === m ? "border-accent bg-accent text-white" : "border-line text-muted"
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-3.5">
            {results.length === 0 && <p className="text-sm text-muted">Sin resultados.</p>}
            <div className="flex flex-col gap-2">
              {results.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className="flex items-center gap-3 rounded-inner border border-line bg-bg1 p-2 text-left"
                >
                  <ExerciseGif urls={e.imageUrls} alt={e.name} className="h-14 w-14 shrink-0 rounded-md" />
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-medium">{e.name}</div>
                    <div className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-muted">
                      {e.muscleGroup} · {e.equipment}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ExerciseDetail({
  entry,
  dayLabel,
  onBack,
  onClose,
  onAdd,
}: {
  entry: LibraryExercise;
  dayLabel: string | null;
  onBack: () => void;
  onClose: () => void;
  onAdd: (entry: LibraryExercise, targets: Targets, dayLabel: string | null) => Promise<void>;
}) {
  const [sets, setSets] = useState("3");
  const [repsMin, setRepsMin] = useState("8");
  const [repsMax, setRepsMax] = useState("12");
  const [rir, setRir] = useState("2");
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    setSaving(true);
    try {
      await onAdd(
        entry,
        {
          sets: Number(sets) || 3,
          repsMin: Number(repsMin) || 8,
          repsMax: Number(repsMax) || 12,
          rir: rir ? Number(rir) : null,
        },
        dayLabel
      );
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-center justify-between border-b border-line p-3.5">
        <button onClick={onBack} className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
          ← Volver
        </button>
        <button onClick={onClose} className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted">
          Cerrar
        </button>
      </div>

      <ExerciseGif urls={entry.imageUrls} alt={entry.name} className="h-56 w-full" />

      <div className="p-3.5">
        <h2 className="font-display text-2xl font-extrabold uppercase">{entry.name}</h2>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <span className="rounded-full border border-accent-bright/25 bg-accent-soft px-2 py-[3px] font-mono text-[9.5px] capitalize text-accent-bright">
            {entry.muscleGroup}
          </span>
          <span className="rounded-full border border-line px-2 py-[3px] font-mono text-[9.5px] capitalize text-muted">
            {entry.equipment}
          </span>
        </div>

        <ol className="mt-4 list-decimal space-y-1.5 pl-4 text-[13.5px] text-text/90">
          {entry.instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>

        <div className="mt-5 rounded-inner border border-line bg-bg1 p-3">
          <p className="mb-2.5 font-mono text-[9px] uppercase tracking-[0.12em] text-muted">
            Objetivo para tu programa
          </p>
          <div className="grid grid-cols-4 gap-2">
            <NumField label="Series" value={sets} onChange={setSets} />
            <NumField label="Reps min" value={repsMin} onChange={setRepsMin} />
            <NumField label="Reps max" value={repsMax} onChange={setRepsMax} />
            <NumField label="RIR" value={rir} onChange={setRir} />
          </div>
        </div>

        <CtaButton className="mb-6" disabled={saving} onClick={handleAdd}>
          {saving ? "Añadiendo…" : "Añadir a mi programa"}
        </CtaButton>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[8px] uppercase tracking-[0.08em] text-muted">{label}</span>
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-line bg-transparent px-2 py-1.5 text-center text-sm text-text outline-none focus:border-accent"
      />
    </label>
  );
}
